import React, { createContext, useContext, useState, useEffect } from 'react'
import { SecretMenuItem } from '../lib/supabase'

export interface CartItem {
  menuItem: SecretMenuItem
  quantity: number
}

interface CartContextType {
  items: CartItem[]
  totalItems: number
  subtotal: number  // Tax & Tip: Sum of item prices
  taxRate: number  // Tax & Tip: Rate from event (e.g., 0.095)
  taxAmount: number  // Tax & Tip: Calculated tax
  tipAmount: number  // Tax & Tip: User-selected tip
  totalAmount: number  // Tax & Tip: subtotal + tax + tip
  totalLobsterTails: number  // Phase 4: Total lobster tails in cart
  eventId: string | null

  // Cart actions
  addItem: (menuItem: SecretMenuItem, quantity?: number) => void
  removeItem: (menuItemId: string) => void
  updateQuantity: (menuItemId: string, quantity: number) => void
  clearCart: () => void
  setEventId: (eventId: string) => void

  // Tax & Tip actions
  setTaxRate: (rate: number) => void
  setTipAmount: (amount: number) => void

  // Helpers
  getItemQuantity: (menuItemId: string) => number
  isInCart: (menuItemId: string) => boolean
}

const CartContext = createContext<CartContextType | null>(null)

export const useCart = () => {
  const context = useContext(CartContext)
  if (!context) {
    throw new Error('useCart must be used within a CartProvider')
  }
  return context
}

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [items, setItems] = useState<CartItem[]>([])
  const [eventId, setEventId] = useState<string | null>(null)
  const [taxRate, setTaxRate] = useState(0.095)  // Default 9.5% CA tax
  const [tipAmount, setTipAmount] = useState(0)

  // Load cart from localStorage on mount
  useEffect(() => {
    const savedCart = localStorage.getItem('secret-cart')
    const savedEventId = localStorage.getItem('secret-cart-event')
    
    if (savedCart) {
      try {
        const parsedCart = JSON.parse(savedCart)
        setItems(parsedCart)
      } catch (error) {
        console.error('Failed to load cart from localStorage:', error)
      }
    }
    
    if (savedEventId) {
      setEventId(savedEventId)
    }
  }, [])

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('secret-cart', JSON.stringify(items))
    if (eventId) {
      localStorage.setItem('secret-cart-event', eventId)
    } else {
      localStorage.removeItem('secret-cart-event')
    }
  }, [items, eventId])

  // Calculate totals
  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0)
  const subtotal = items.reduce((sum, item) => sum + (item.menuItem.price * item.quantity), 0)
  const taxAmount = Math.round(subtotal * taxRate * 100) / 100
  const totalAmount = subtotal + taxAmount + tipAmount

  // Phase 4: Calculate total lobster tails in cart
  const totalLobsterTails = items.reduce((sum, item) => {
    const tailQty = item.menuItem.lobster_tail_qty || 0
    return sum + (tailQty * item.quantity)
  }, 0)

  const addItem = (menuItem: SecretMenuItem, quantity = 1) => {
    setItems(prevItems => {
      const existingItemIndex = prevItems.findIndex(item => item.menuItem.id === menuItem.id)

      if (existingItemIndex >= 0) {
        // Update existing item
        const updatedItems = [...prevItems]
        updatedItems[existingItemIndex] = {
          ...updatedItems[existingItemIndex],
          quantity: updatedItems[existingItemIndex].quantity + quantity
        }
        return updatedItems
      } else {
        // Add new item
        return [
          ...prevItems,
          {
            menuItem,
            quantity
          }
        ]
      }
    })

    // Set event ID if not already set
    if (!eventId && menuItem.event_id) {
      setEventId(menuItem.event_id)
    }
  }

  const removeItem = (menuItemId: string) => {
    setItems(prevItems => prevItems.filter(item => item.menuItem.id !== menuItemId))
  }

  const updateQuantity = (menuItemId: string, quantity: number) => {
    if (quantity <= 0) {
      removeItem(menuItemId)
      return
    }

    setItems(prevItems =>
      prevItems.map(item =>
        item.menuItem.id === menuItemId
          ? { ...item, quantity }
          : item
      )
    )
  }

  const clearCart = () => {
    setItems([])
    setEventId(null)
    setTipAmount(0)
    localStorage.removeItem('secret-cart')
    localStorage.removeItem('secret-cart-event')
  }

  const getItemQuantity = (menuItemId: string): number => {
    const item = items.find(item => item.menuItem.id === menuItemId)
    return item ? item.quantity : 0
  }

  const isInCart = (menuItemId: string): boolean => {
    return items.some(item => item.menuItem.id === menuItemId)
  }

  const handleSetEventId = (newEventId: string) => {
    // If changing events, clear the cart
    if (eventId && eventId !== newEventId && items.length > 0) {
      const confirmClear = window.confirm(
        'Changing events will clear your current cart. Continue?'
      )
      if (confirmClear) {
        clearCart()
        setEventId(newEventId)
      }
    } else {
      setEventId(newEventId)
    }
  }

  return (
    <CartContext.Provider
      value={{
        items,
        totalItems,
        subtotal,
        taxRate,
        taxAmount,
        tipAmount,
        totalAmount,
        totalLobsterTails,  // Phase 4
        eventId,
        addItem,
        removeItem,
        updateQuantity,
        clearCart,
        setEventId: handleSetEventId,
        setTaxRate,
        setTipAmount,
        getItemQuantity,
        isInCart
      }}
    >
      {children}
    </CartContext.Provider>
  )
}

export default CartProvider